/**
 * AI CONTEXT
 * Purpose: read-only student selectors from Jotai atoms (no extra subscriptions).
 * Exposes: get all/some/one student by atom lookups.
 * Used by: many forms/views requiring student lookups.
 * Importance: avoids duplicate listener churn for frequent student access.
 * Refactor: mirror this selector pattern for other high-traffic collections.
 */
import { atom, useAtomValue } from "jotai"
import { useMemo } from "react"

import { studentAtoms } from "#atoms"
import { StudentConstructor } from "#customTypes/composedTypes/studentTypes"
import { Uuid } from "#customTypes/globalTypes"

import { useStableIds } from "./useSubscription"

/**
 * Student hooks - Read-only from Jotai atoms
 *
 * FirebaseContextProvider already calls useAllDocs for students,
 * so these hooks just read from the already-populated atoms.
 * NO additional subscriptions are created.
 */

export const useGetAllStudents = (): StudentConstructor[] => {
  return useAtomValue(studentAtoms.allDocsAtom)
}

export const useGetSomeStudents = (ids: Uuid[]): StudentConstructor[] => {
  const stableIds = useStableIds(ids)
  const studentsAtom = useMemo(
    () =>
      atom((get) =>
        stableIds
          .map((id) => get(studentAtoms.docByIdAtom(id)))
          .filter((doc): doc is StudentConstructor => doc !== null)
      ),
    [stableIds]
  )

  return useAtomValue(studentsAtom)
}

export const useGetOneStudent = (
  id: Uuid | undefined | null
): StudentConstructor | null => {
  return useAtomValue(studentAtoms.docByIdAtom(id ?? null))
}
